import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { adminAgentService, type AdminAction } from '../../../../services/adminAgent.service';

const ACTIONS_QUERY_ROOT = ['admin-agent', 'actions'] as const;
const ACTIONS_POLL_INTERVAL_MS = 5000;

/**
 * Changes proposed by the agent. The chat stream carries only text, so approvals come from the
 * REST list: polled while the panel is open and refreshed as soon as a reply finishes.
 */
export function useAdminActions(options: {
  open: boolean;
  isStreaming: boolean;
  conversationPublicId: string;
}) {
  const queryClient = useQueryClient();
  const { open, isStreaming, conversationPublicId } = options;
  const actionsQueryKey = [...ACTIONS_QUERY_ROOT, conversationPublicId] as const;

  const query = useQuery({
    queryKey: actionsQueryKey,
    queryFn: () => adminAgentService.listActions({ conversationPublicId }),
    enabled: open,
    refetchInterval: open ? ACTIONS_POLL_INTERVAL_MS : false,
  });

  useEffect(() => {
    if (open && !isStreaming) void queryClient.invalidateQueries({ queryKey: actionsQueryKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, open, queryClient, conversationPublicId]);

  const replaceAction = (updated: AdminAction) =>
    queryClient.setQueryData<AdminAction[]>(actionsQueryKey, (current = []) =>
      current.map((action) => (action.publicId === updated.publicId ? updated : action))
    );

  const approve = useMutation({
    mutationFn: (input: { publicId: string; confirmed: boolean }) =>
      adminAgentService.approveAction(input.publicId, input.confirmed),
    onSuccess: replaceAction,
    onSettled: () => queryClient.invalidateQueries({ queryKey: actionsQueryKey }),
  });

  const reject = useMutation({
    mutationFn: (publicId: string) => adminAgentService.rejectAction(publicId),
    onSuccess: replaceAction,
    onSettled: () => queryClient.invalidateQueries({ queryKey: actionsQueryKey }),
  });

  return { actions: query.data ?? [], approve, reject };
}
